/**
 * Video on a call, as the UI sees it
 *
 * The video state (useVideoCallsStore) and SipClient's camera methods are keyed by the SIP session's own Call-ID. The
 * realtime call events carry the Call-ID of the caller's leg on every leg, so for a call I received it is not my
 * session's. UI code maps a realtime call to its video state here, and nowhere else.
 */

import { ref } from 'vue'
import SipClient from '@/utilities/SipClient'
import globalEvents from '@/utilities/GlobalEvents'
import logger from '@/utilities/Logger'
import { cameraInUse, useVideoCallsStore, type CallVideoState } from '@/stores/VideoCalls'
import { jssipSupportsVideo } from '@/utilities/video/capabilities'
import { useMediaDevicesStore } from '@/stores/MediaDevices'
import { useSettingsStore } from '@/stores/Settings'
import { useElectronStore } from '@/stores/Electron'
import { PbxCallState } from '@/TcxApi/PbxCommon'
import type { PbxAppEmployeesExtensionCallWithDate } from '@/TcxApi/PbxApp'

/** The parts of a realtime call that find its softphone session */
export type SessionLookupCall = Pick<PbxAppEmployeesExtensionCallWithDate, 'callId' | 'caller' | 'state'>

/** The parts of a realtime call the camera button looks at */
export type CameraCall = SessionLookupCall & Pick<PbxAppEmployeesExtensionCallWithDate, 'peerVideo'>

/** The parts of a realtime call that say who the other party is and whether video can reach them */
export type PeerCall = CameraCall & Pick<PbxAppEmployeesExtensionCallWithDate, 'leg' | 'callee'>

// SipClient's sessions are not reactive: every lookup depends on this, bumped at each SIP event - one comes with every
// change of a session's state, which is what the lookup of a call I received goes by
const sipTick = ref(0)

globalEvents.on('SipClient', () => { sipTick.value++ })

/**
 * The softphone session of a realtime call - found the way the call panel finds it: by Call-ID (a call I made), else
 * by caller number and state (a call I received)
 * @param call The realtime call
 * @returns The session, undefined when the call isn't on this softphone
 */
export function sessionFor(call: SessionLookupCall) {
	void sipTick.value
	return SipClient.instance.getSession(call.callId, call.caller.number, call.state)
}

/**
 * The key of a realtime call's video state: its SIP session's own Call-ID
 * @param call The realtime call
 * @returns The session's Call-ID, undefined when the call isn't on this softphone
 */
export function sipCallIdFor(call: SessionLookupCall): string | undefined {
	return sessionFor(call)?.data.callId
}

/**
 * A realtime call's video state
 * @param call The realtime call
 * @returns The state, undefined when the call isn't on this softphone or SipVideo doesn't handle it
 */
export function videoStateFor(call: SessionLookupCall): CallVideoState | undefined {
	const callId = sipCallIdFor(call)

	return callId ? useVideoCallsStore().calls[callId] : undefined
}

/**
 * Whether my camera can be turned on for a call (spec §7.3): the installed JsSIP can carry video, the other party can
 * do video - the PBX publishes peerVideo only then - the call is answered and not on hold, a camera is there to use and
 * wasn't turned down with "Benyt ikke", and video wasn't rejected earlier in the call
 * @param call The realtime call
 */
export function canUseCamera(call: CameraCall): boolean {
	if (!jssipSupportsVideo()) { return false }
	if (!call.peerVideo) { return false }
	if (call.state !== PbxCallState.CALL_ANSWER) { return false } // CALL_HOLD is how a held call shows
	if (useMediaDevicesStore().videoDeviceId === null) { return false }

	const video = videoStateFor(call)

	return !!video && !video.unavailable
}

/**
 * Whether a call shows the camera button - never with a JsSIP that can't carry video, nor unless the other party can do
 * video. On an answered call: when the camera can be used, always while it is on - so it can be turned off whatever
 * changed meanwhile - and, disabled, once video was rejected on the call (spec §10.4). Only "Benyt ikke" or having no
 * camera hides it then. On hold only while my camera is on, to turn it off: the other party may hold the call - my own
 * hold turns the camera off - and it can't be turned on while held.
 * @param call The realtime call
 */
export function showCameraButton(call: CameraCall): boolean {
	if (!jssipSupportsVideo() || !call.peerVideo) { return false }
	if (call.state !== PbxCallState.CALL_ANSWER && call.state !== PbxCallState.CALL_HOLD) { return false }

	const video = videoStateFor(call)

	if (!video) { return false }
	if (call.state === PbxCallState.CALL_HOLD) { return cameraInUse(video) }
	return cameraInUse(video) || useMediaDevicesStore().videoDeviceId !== null
}

/**
 * Turn my camera on for a call, or off when it is on - on only when canUseCamera allows it, so not while the call is
 * held, whatever the button showed a moment ago
 * @param call The realtime call
 * @param from Where the user clicked, for the log
 */
export function toggleCamera(call: CameraCall, from: string) {
	const callId = sipCallIdFor(call)

	if (!callId) { return }

	const turnOn = !cameraInUse(useVideoCallsStore().calls[callId])
	const sip = SipClient.instance

	if (turnOn && !canUseCamera(call)) {
		logger.info(`${from}: The camera can not be turned on for call ${callId} now - ignoring the click`)
		return
	}

	logger.info(`${from}: User turns the camera ${turnOn ? 'on' : 'off'} for call ${callId}`)
	// The bitrate cap goes by the other party's device class: hand it over again, in case the call event found no session
	if (turnOn) { sip.setPeerVideo(callId, call.peerVideo) }
	const change = turnOn ? sip.enableCamera(callId) : sip.disableCamera(callId)

	change.catch((err: unknown) => { logger.warning(`${from}: Turning the camera ${turnOn ? 'on' : 'off'} failed - ${(err as Error).message}`) })
}

/**
 * Whether the screen can be shared here (screen-share spec §4.4): in the Communicator Desktop app only when the app has the
 * screen-share bridge (an older app has no source picker), in a browser when it has getDisplayMedia
 */
export function screenShareSupported(): boolean {
	if (useSettingsStore().runtime.isEmbedded) { return useElectronStore().supportsScreenShare }
	return typeof navigator.mediaDevices?.getDisplayMedia === 'function'
}

/**
 * Whether my screen can be shared on a call: the camera button's conditions (canUseCamera) without the need for a camera,
 * where screen capture works
 * @param call The realtime call
 */
export function canShareScreen(call: CameraCall): boolean {
	if (!jssipSupportsVideo() || !call.peerVideo || !screenShareSupported()) { return false }
	if (call.state !== PbxCallState.CALL_ANSWER) { return false }

	const video = videoStateFor(call)

	return !!video && !video.unavailable
}

/**
 * Share my screen on a call, or stop sharing it
 * @param call The realtime call
 * @param from Where the user clicked, for the log
 * @param via The window the user clicked in - the pop-out or picture-in-picture window, where the view is there
 */
export function toggleScreenShare(call: CameraCall, from: string, via?: Window) {
	const callId = sipCallIdFor(call)

	if (!callId) { return }

	const sharing = useVideoCallsStore().calls[callId]?.source === 'screen'
	const sip = SipClient.instance

	if (!sharing && !canShareScreen(call)) {
		logger.info(`${from}: The screen can not be shared on call ${callId} now - ignoring the click`)
		return
	}

	logger.info(`${from}: User ${sharing ? 'stops sharing' : 'shares'} the screen on call ${callId}`)
	if (!sharing) { sip.setPeerVideo(callId, call.peerVideo) }
	const change = sharing ? sip.stopScreenShare(callId) : sip.shareScreen(callId, via)

	change.catch((err: unknown) => { logger.warning(`${from}: ${sharing ? 'Stopping' : 'Starting'} screen sharing failed - ${(err as Error).message}`) })
}

/**
 * The other party's number - the callee on a call I made, the caller on one I received
 * @param call The realtime call
 */
function otherPartyNumber(call: PeerCall) {
	return call.leg === 'I' ? call.callee.number : call.caller.number
}

/**
 * Follow a CALL_ANSWER or CALL_UPDATE of one of my calls into its video: record the other party's device class, and
 * when the other party changed - another number, or another or no device class (a transfer, a pickup, a replaced leg) -
 * turn my camera off first, so the new party never gets it unasked (spec §10.2). A device class named for the first
 * time is no change: the PBX names it first on the CALL_ANSWER, which can come after a guest call turned my camera on
 * at 200 OK.
 * @param call The call as it is now
 * @param previous The call as it was before the event, if it was known
 */
export function followCallVideo(call: PeerCall, previous?: PeerCall) {
	// The session was set up with the caller it had then - an update may already name someone else
	const callId = sipCallIdFor({ callId: call.callId, caller: previous?.caller ?? call.caller, state: call.state })

	if (!callId) { return }

	const video = useVideoCallsStore().calls[callId]
	const otherDevice = !!previous?.peerVideo && previous.peerVideo !== call.peerVideo
	const peerChanged = !!previous && (otherPartyNumber(previous) !== otherPartyNumber(call) || otherDevice)

	if (peerChanged && (cameraInUse(video) || video?.resumeCamera)) {
		SipClient.instance.onPeerChanged(callId)
	}
	SipClient.instance.setPeerVideo(callId, call.peerVideo)
}
